import type { TourKey, TourOutcome } from 'definitions';
import type { TourState } from 'types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useClient } from 'hooks/useClient';
import { useGlobal } from 'hooks/useGlobal';
import type { RunningTour } from './tour-types';

const TOURS_KEY = ['TOURS'];

type MarkSeenParams = { tourKey: TourKey; version: number; outcome: TourOutcome };

/**
 * The signed-in user's tour state, the tour on screen, and how to record one.
 *
 * The running tour is global state so the Help menu can start one and the layer can
 * draw it from anywhere in the shell.
 */
export const useTours = () => {
  const apiClient = useClient();
  const queryClient = useQueryClient();
  const [running, setRunning] = useGlobal<RunningTour | null>('TOUR_RUNNING', null);

  const { data: state } = useQuery<TourState>({
    queryKey: TOURS_KEY,
    queryFn: () => apiClient.get<TourState>('/tours'),
    staleTime: Infinity,
  });

  /**
   * Written to the cache first, so the tour cannot start again while the request is in
   * flight, or if it fails. A failure costs at most one repeat on the next sign-in.
   */
  const markSeen = async ({ tourKey, version, outcome }: MarkSeenParams) => {
    queryClient.setQueryData<TourState>(TOURS_KEY, (previous) =>
      previous ? { ...previous, seen: { ...previous.seen, [tourKey]: { version, outcome } } } : previous,
    );
    try {
      await apiClient.post('/tours/seen', { tourKey, version, outcome });
    } catch (error) {
      console.error('Could not record the tour as seen', tourKey, error);
    }
  };

  return { state, running, setRunning, markSeen };
};
